/**
 * Home (/): "Næst á dagskrá" hero + month calendar + agenda.
 * iPad landscape (≥ 1024 px): month grid left; hero and selected day / agenda right.
 * Portrait / phone: hero, compact month (dots), then the selected day or the next 30 days.
 * One column on phones is `grid-cols-1` (minmax(0, 1fr)), never the implicit `auto` track: that one grows
 * to the full width of any one-line truncated text (a long event title or place) and the page scrolls sideways.
 */
import { useQueryClient } from '@tanstack/react-query';
import { CalendarX2, ListChecks, WifiOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { prefetchEvents, useEvents, useServerNow } from '../api/calendar';
import { Page } from '../components/layout';
import { Button, Card, EmptyState, ErrorBox, Spinner } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { addDays, addMonths, dayHeading, monthGridRange, parseDateStr, todayStr, weekStart, ymOf } from '../lib/dates';
import { AgendaList, EventRow } from './calendar/Agenda';
import { MonthCalendar } from './calendar/MonthCalendar';
import { NewEventFab } from './calendar/NewEventFab';
import { NextUpCard } from './calendar/NextUpCard';
import { useQueuedRejectionToasts } from './calendar/participation';
import { errorText, useIsEditor } from './calendar/shared';
import { PracticeHomeCard } from '../practice/PracticeMenu';

export default function HomeScreen() {
  const t = useT();
  const { loc } = useI18n();
  const qc = useQueryClient();
  const editor = useIsEditor();
  const now = useServerNow(30_000);
  const today = todayStr(now);
  const [ym, setYmState] = useState(() => ymOf(today));
  const [selected, setSelected] = useState<string | null>(null);
  const dayPanel = useRef<HTMLElement>(null);
  useQueuedRejectionToasts();

  // Upcoming window anchored to this week's Monday so the cache key changes weekly, not daily.
  // Ranges are inclusive dates (server contract); 7 weeks covers today + 30 days and the hero.
  const upFrom = weekStart(today);
  const upcoming = useEvents(upFrom, addDays(upFrom, 48));
  const grid = monthGridRange(ym);
  const month = useEvents(grid.from, grid.to);

  useEffect(() => {
    for (const d of [-1, 1]) {
      const r = monthGridRange(addMonths(ym, d));
      prefetchEvents(qc, r.from, r.to);
    }
  }, [ym, qc]);

  const setYm = (next: string) => {
    setYmState(next);
    const r = monthGridRange(next);
    setSelected((s) => (s && s >= r.from && s <= r.to ? s : null));
  };

  const upcomingList = useMemo(() => (upcoming.data ?? []).filter((e) => e.endsAt > now), [upcoming.data, now]);
  const next = upcomingList.find((e) => e.status === 'scheduled') ?? upcomingList[0] ?? null;
  const agenda = useMemo(() => {
    const end = parseDateStr(addDays(today, 30));
    return upcomingList.filter((e) => e.startsAt < end);
  }, [upcomingList, today]);
  const dayEvents = useMemo(() => {
    if (!selected) return [];
    const from = parseDateStr(selected);
    const source = month.data ?? upcoming.data ?? [];
    return source.filter((e) => e.startsAt >= from && e.startsAt < from + 86_400_000);
  }, [selected, month.data, upcoming.data]);

  const select = (d: string | null) => {
    setSelected(d);
    if (d && !window.matchMedia('(min-width: 1024px)').matches) {
      requestAnimationFrame(() => dayPanel.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    }
  };

  const goToday = () => {
    setYmState(ymOf(today));
    setSelected(null);
  };

  let hero;
  if (next) hero = <NextUpCard ev={next} now={now} />;
  else if (upcoming.isPending && upcoming.fetchStatus === 'paused')
    hero = (
      <Card>
        <EmptyState icon={WifiOff} title={t('common.offline')}>
          {t('common.no_data_offline')}
        </EmptyState>
      </Card>
    );
  else if (upcoming.isPending)
    hero = (
      <Card className="flex min-h-40 items-center justify-center p-6">
        <Spinner label={t('common.loading')} />
      </Card>
    );
  else if (upcoming.isError)
    hero = (
      <ErrorBox onRetry={() => void upcoming.refetch()} retryLabel={t('common.retry')}>
        {t('calendar.load_error')} {errorText(t, upcoming.error)}
      </ErrorBox>
    );
  else
    hero = (
      <Card>
        <EmptyState icon={CalendarX2} title={t('calendar.nothing_upcoming')}>
          {t('calendar.nothing_upcoming_hint')}
        </EmptyState>
      </Card>
    );

  return (
    <Page brand title={t('calendar.title')} wide>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start" style={{ paddingBottom: editor ? 88 : undefined }}>
        <div className="flex flex-col gap-6 lg:col-start-2 lg:row-start-1">
          {hero}
          <PracticeHomeCard />
        </div>

        <div className="lg:sticky lg:top-24 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <MonthCalendar
            ym={ym}
            onYm={setYm}
            onToday={goToday}
            selected={selected}
            onSelect={select}
            today={today}
            events={month.data}
            loading={month.isFetching && !month.data}
          />
          {month.isError && !month.data ? (
            <div className="mt-3">
              <ErrorBox onRetry={() => void month.refetch()} retryLabel={t('common.retry')}>
                {t('calendar.load_error')}
              </ErrorBox>
            </div>
          ) : null}
        </div>

        <section ref={dayPanel} className="scroll-mt-24 lg:col-start-2 lg:row-start-2" aria-live="polite">
          {selected ? (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h2 className="min-w-0 flex-1 text-[20px] font-bold">{dayHeading(selected, today, loc)}</h2>
                <Button variant="ghost" size="sm" icon={ListChecks} className="min-h-11" onClick={() => setSelected(null)}>
                  {t('calendar.show_agenda')}
                </Button>
              </div>
              {dayEvents.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {dayEvents.map((ev) => (
                    <li key={ev.id}>
                      <EventRow ev={ev} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-2xl border border-dashed border-[var(--line)] px-4 py-8 text-center text-[var(--muted)]">
                  {!month.data && month.fetchStatus === 'fetching' ? t('common.loading') : t('calendar.day_empty')}
                </p>
              )}
            </>
          ) : (
            <>
              <h2 className="mb-3 text-[20px] font-bold">{t('calendar.agenda_title')}</h2>
              {upcoming.data ? (
                <AgendaList events={agenda} today={today} empty={t('calendar.agenda_empty')} />
              ) : upcoming.fetchStatus === 'fetching' ? (
                <Spinner label={t('common.loading')} />
              ) : null}
            </>
          )}
        </section>
      </div>
      {editor ? (
        <NewEventFab
          defaultDate={selected ?? undefined}
          onCreated={(date) => {
            setYmState(ymOf(date));
            setSelected(date);
          }}
        />
      ) : null}
    </Page>
  );
}
